import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const services = [
  {
    title: 'Luxury Home Sales',
    desc: 'Strategic listing, pricing, and positioning designed to maximize property value and exposure. Every listing receives a custom marketing plan, professional presentation, and targeted outreach to qualified buyers in the Lake Tahoe luxury market.',
    icon: 'ri-home-4-line',
    image:
      'https://public.readdy.ai/ai/img_res/3e6209027bc73a7b2157ee241393a18f.jpg',
  },
  {
    title: 'Buyer Representation',
    desc: 'Access to exclusive Lake Tahoe listings with expert negotiation and deep market insight. From initial search through closing, every buyer receives personalized guidance tailored to their lifestyle goals and investment criteria.',
    icon: 'ri-search-eye-line',
    image:
      'https://readdy.ai/api/search-image?query=happy%20affluent%20couple%20viewing%20luxury%20Lake%20Tahoe%20lakefront%20property%20with%20real%20estate%20agent%20dock%20panoramic%20water%20views%20blue%20sky%20California%20aspirational%20lifestyle%20high%20end%20photography&width=900&height=600&seq=srv-s2-ck&orientation=landscape',
  },
  {
    title: 'Vacation & Second Homes',
    desc: 'Specialized guidance for second-home buyers navigating Tahoe\'s unique seasonal market. Expertise in resort community rules, short-term rental potential, and the lifestyle factors that define the perfect alpine retreat in the Sierra Nevada.',
    icon: 'ri-sun-line',
    image:
      'https://readdy.ai/api/search-image?query=cozy%20luxurious%20ski%20lodge%20interior%20rustic%20modern%20design%20warm%20fireplace%20mountain%20view%20windows%20plush%20furnishings%20Kirkwood%20Lake%20Tahoe%20California%20winter%20retreat%20lifestyle%20photography&width=900&height=600&seq=srv-s3-ck&orientation=landscape',
  },
  {
    title: 'Investment Properties',
    desc: 'Expert advising on high-performing real estate opportunities in Lake Tahoe with strong long-term upside. In-depth analysis of rental yield, appreciation potential, and market timing across the Sierra Nevada region.',
    icon: 'ri-funds-line',
    image:
      'https://readdy.ai/api/search-image?query=aerial%20view%20luxury%20mountain%20resort%20community%20Kirkwood%20California%20ski%20slopes%20chalets%20pine%20trees%20snow%20winter%20investment%20property%20real%20estate%20drone%20photography%20premium%20community&width=900&height=600&seq=srv-s4-ck&orientation=landscape',
  },
];

const processSteps = ['Consultation', 'Strategy', 'Execution', 'Closing'];

const faqs = [
  {
    q: 'What services does Cam Kirkwood offer buyers in Lake Tahoe?',
    a: 'Cam provides full-service buyer representation including property search, market analysis, expert negotiation, due diligence coordination, and post-closing advisory support. Every buyer receives a custom strategy aligned with their lifestyle goals and investment criteria in Kirkwood and the greater Tahoe region.',
  },
  {
    q: 'How does Cam Kirkwood market luxury listings in Kirkwood?',
    a: 'Each Kirkwood listing receives a bespoke marketing plan featuring professional photography, targeted digital campaigns, network outreach to qualified buyers, and precision pricing. Cam\'s elevated strategy is designed to attract the right audience and maximize final sale price with minimal time on market.',
  },
  {
    q: 'Can Cam Kirkwood help with investment properties near Lake Tahoe?',
    a: 'Yes. Cam provides in-depth investment analysis including rental income potential, appreciation forecasts, and market timing insights for properties across Kirkwood, South Lake Tahoe, and surrounding alpine communities. Every investment recommendation is supported by local market data and decades of Sierra Nevada real estate expertise.',
  },
];

const testimonials = [
  {
    quote: 'Cam\'s knowledge of the Kirkwood market is unparalleled. The listing process was flawless and we sold above asking in under 30 days.',
    author: 'L. & P. Crawford',
    location: 'Kirkwood, CA',
    stars: 5,
  },
  {
    quote: 'From first showing to closing day, Cam was responsive, strategic, and deeply knowledgeable about Lake Tahoe real estate. Couldn\'t recommend more highly.',
    author: 'J. Montrose',
    location: 'South Lake Tahoe, CA',
    stars: 5,
  },
  {
    quote: 'We found our dream second home in Alpine County thanks to Cam\'s off-market connections. Exceptional service from start to finish.',
    author: 'D. & M. Ashford',
    location: 'Alpine County, CA',
    stars: 5,
  },
];

const whyUs = [
  { icon: 'ri-map-pin-2-line', title: 'Local Expertise', text: 'Deep knowledge of Lake Tahoe and Sierra Nevada resort market dynamics.' },
  { icon: 'ri-scales-3-line', title: 'Precision Pricing', text: 'Data-backed analysis for optimal outcomes in every transaction.' },
  { icon: 'ri-megaphone-line', title: 'Elite Marketing', text: 'High-impact positioning for luxury buyers and sellers in Tahoe.' },
  { icon: 'ri-vip-crown-line', title: 'Concierge Service', text: 'White-glove guidance with discretion and professionalism throughout.' },
];

const serviceAreas = ['South Lake Tahoe', 'North Lake Tahoe', 'Alpine County', 'El Dorado County', 'Sierra Nevada'];

export default function ServicesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="font-sans bg-warm-white overflow-x-hidden">
      <Header />

      {/* 1. HERO */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=panoramic%20luxury%20Lake%20Tahoe%20California%20property%20scenic%20overlook%20dramatic%20sky%20Sierra%20Nevada%20mountains%20cinematic%20real%20estate%20photography%20wide%20angle%20golden%20light&width=1920&height=900&seq=srv-hero-ck&orientation=landscape)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/75 via-charcoal/55 to-charcoal/80" />
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-warm-white to-transparent z-10" />
        <div className="relative z-20 max-w-4xl mx-auto text-center px-4 md:px-8 pt-28 pb-16">
          <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-5">What We Offer</p>
          <h1 className="font-serif text-5xl md:text-7xl text-white font-light leading-tight mb-6">
            Luxury Real Estate <em className="italic text-gold">Services</em>
            <span className="block text-2xl md:text-3xl mt-4 font-light text-white/70">in Lake Tahoe &amp; Sierra Nevada</span>
          </h1>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gold/40" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/50" />
            <div className="w-12 h-px bg-gold/40" />
          </div>
          <p className="text-warm-white/65 max-w-xl mx-auto text-base">
            Buying &bull; Selling &bull; Investment &bull; Vacation Homes
          </p>
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <section className="bg-charcoal border-y border-gold/15 py-5 overflow-hidden">
        <div className="flex items-center trust-marquee-svc">
          {['Luxury Home Buyers', 'Sellers', 'Investors', 'Second-Home Owners', '★★★★★ Trusted in Lake Tahoe & Sierra Nevada', 'Local Expertise', 'High-Value Transactions', 'Luxury Home Buyers', 'Sellers', 'Investors', 'Second-Home Owners', '★★★★★ Trusted in Lake Tahoe & Sierra Nevada', 'Local Expertise', 'High-Value Transactions'].map((item, i) => (
            <span key={i} className="flex items-center gap-6 whitespace-nowrap text-warm-white/60 text-[10px] tracking-[0.25em] uppercase font-light px-8">
              {item}<span className="text-gold/40 text-xs">◆</span>
            </span>
          ))}
        </div>
        <style>{`.trust-marquee-svc { animation: marquee 35s linear infinite; } @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
      </section>

      {/* 3. SERVICES DETAIL */}
      <section className="py-24 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Our Specializations</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light">
              Lake Tahoe <em className="italic">Real Estate Services</em>
            </h2>
          </div>
          <div className="space-y-20">
            {services.map((s, i) => (
              <div
                key={s.title}
                className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
              >
                <div className="lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden h-80 w-full"
                    style={{ boxShadow: '0 8px 32px rgba(42,42,42,0.1)' }}>
                    <img src={s.image} alt={`${s.title} in Lake Tahoe and Sierra Nevada`} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
                    <div className="absolute top-5 left-5 w-11 h-11 flex items-center justify-center bg-gold rounded-lg">
                      <i className={`${s.icon} text-white text-lg`} />
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-px bg-gold/50" />
                    <p className="text-[9px] tracking-[0.4em] uppercase text-gold">Service</p>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl text-charcoal font-light mb-5">{s.title}</h2>
                  <p className="text-stone leading-relaxed mb-7 text-sm">{s.desc}</p>
                  <a
                    href="#consultation"
                    className="inline-flex items-center gap-3 whitespace-nowrap px-7 py-3.5 border border-charcoal text-charcoal text-xs tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:bg-charcoal hover:text-warm-white transition-all duration-300"
                  >
                    <span>Discuss This Service</span>
                    <i className="ri-arrow-right-line text-gold" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="bg-gold py-10 px-4 md:px-8 relative z-20">
        <div className="max-w-4xl mx-auto text-center flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-2xl text-white font-light">Ready to Start? Let&apos;s Discuss Your Goals.</p>
          <a href="#consultation" className="whitespace-nowrap px-7 py-4 bg-charcoal text-white text-xs tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:bg-charcoal/85 transition-all duration-300">
            Schedule Consultation
          </a>
        </div>
      </section>

      {/* 4. AI BLOCK */}
      <section className="py-24 px-4 md:px-8 bg-charcoal relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Quick Answers</p>
            <h2 className="font-serif text-4xl text-white font-light">About Our <em className="italic text-gold">Services</em></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { q: 'What services do you offer?', a: 'Luxury home sales, buyer representation, vacation and second-home guidance, and investment property advisory — all focused on Kirkwood and the greater Lake Tahoe region.' },
              { q: 'Who do you work with?', a: 'Buyers, sellers, investors, and second-home purchasers seeking high-end properties in Kirkwood, South Lake Tahoe, Alpine County, and surrounding Sierra Nevada communities.' },
              { q: 'What benefits do clients receive?', a: 'Concierge-level service, local market expertise, precision pricing, elevated marketing, and a results-driven approach backed by 20+ years of Kirkwood and Tahoe real estate experience.' },
              { q: 'How is the process structured?', a: 'Consultation → Strategy → Execution → Close. Every step is customized to your goals, timeline, and property criteria in the Kirkwood and Lake Tahoe market.' },
            ].map((item, i) => (
              <div key={i} className="rounded-xl p-6 border border-white/8 bg-white/3">
                <p className="font-serif text-base text-gold font-medium mb-3">{item.q}</p>
                <p className="text-warm-white/60 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. PROCESS */}
      <section className="py-20 px-4 md:px-8 bg-charcoal border-t border-gold/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-serif text-4xl text-white font-light">The <em className="italic text-gold">Process</em></h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, i) => (
              <div key={step} className="text-center">
                <span className="font-serif text-5xl text-gold/15 block mb-3 font-light">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-warm-white text-sm tracking-wider font-medium">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. WHY CHOOSE US */}
      <section className="py-24 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Why Us</p>
            <h2 className="font-serif text-4xl text-charcoal font-light">Why Choose <em className="italic">Cam Kirkwood</em></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.map((r) => (
              <div key={r.title} className="bg-cream rounded-xl p-6 border border-cream/80">
                <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg mb-4 border border-gold/15">
                  <i className={`${r.icon} text-gold text-lg`} />
                </div>
                <h3 className="font-serif text-base text-charcoal font-medium mb-2">{r.title}</h3>
                <p className="text-stone text-sm leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. SERVICE AREAS */}
      <section className="py-16 px-4 md:px-8 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-3">Where We Serve</p>
            <h2 className="font-serif text-3xl text-charcoal font-light">Service <em className="italic">Areas</em></h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {serviceAreas.map((area) => (
              <div key={area} className="bg-white rounded-lg px-5 py-3 border border-cream/80">
                <i className="ri-map-pin-line text-gold mr-2 text-sm" />
                <span className="text-charcoal text-sm font-medium">{area}</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl overflow-hidden h-72 md:h-80 border border-gold/10">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d51093.23504258905!2d-120.12497282028503!3d38.68189099999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80995413b10f0db5%3A0x5b7b3a0b0b0b0b0b!2sKirkwood%2C%20CA%2095646!5e0!3m2!1sen!2sus!4v1711000000000!5m2!1sen!2sus"
              className="w-full h-full"
              title="Kirkwood and Lake Tahoe Real Estate Service Area Map"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      <section className="py-24 px-4 md:px-8 bg-charcoal">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Client Voices</p>
            <h2 className="font-serif text-4xl text-white font-light">What <em className="italic text-gold">Clients Say</em></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-taupe rounded-2xl p-7 border border-white/5">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <i key={si} className="ri-star-fill text-gold text-sm" />
                  ))}
                </div>
                <i className="ri-double-quotes-l text-gold/25 text-4xl block mb-3" />
                <p className="text-warm-white/65 text-sm leading-relaxed mb-6 italic">{t.quote}</p>
                <div className="border-t border-white/10 pt-4">
                  <p className="font-serif text-white font-medium">{t.author}</p>
                  <p className="text-gold/60 text-xs mt-0.5">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-24 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-4 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-3">Questions</p>
            <h2 className="font-serif text-4xl text-charcoal font-light">Service <em className="italic">FAQs</em></h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-cream bg-white">
                <button className="w-full flex justify-between items-start px-6 py-5 text-left cursor-pointer gap-4" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-serif text-lg text-charcoal font-medium leading-snug">{f.q}</span>
                  <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border mt-0.5 transition-all duration-300 ${openFaq === i ? 'border-gold bg-gold text-white rotate-45' : 'border-stone/25 text-stone'}`}>
                    <i className="ri-add-line text-sm" />
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? 'max-h-48' : 'max-h-0'}`}>
                  <p className="px-6 pb-5 text-stone text-sm leading-relaxed">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section id="consultation" className="relative py-20 px-4 md:px-8 bg-charcoal text-center overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=luxury%20alpine%20Lake%20Tahoe%20estate%20night%20dramatic%20starry%20sky%20reflection%20moody%20cinematic%20real%20estate&width=1920&height=600&seq=srv-cta-ck&orientation=landscape)' }}
        />
        <div className="relative z-10 max-w-2xl mx-auto">
          <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Ready to Begin?</p>
          <h2 className="font-serif text-4xl md:text-5xl text-white font-light mb-5">
            Start Your <em className="italic text-gold">Real Estate Journey</em>
          </h2>
          <p className="text-warm-white/60 mb-10 leading-relaxed">
            Schedule a private consultation to discuss your goals in the Kirkwood and Lake Tahoe luxury real estate market.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:5617146589"
              className="whitespace-nowrap px-8 py-4 border border-warm-white/25 text-warm-white text-xs tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:border-gold hover:text-gold transition-all duration-300"
            >
              <i className="ri-phone-line mr-2 text-gold" />
              Call 561-714-6589
            </a>
            <Link
              to="/#consultation"
              className="whitespace-nowrap px-8 py-4 bg-gold text-white text-xs tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:bg-[#b8983a] transition-colors duration-300"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}